import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon16Alert,
  Icon16Checkmark,
  Icon24ArrowRight,
  Typography,
} from "@cur8/maneki";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useMemo } from "react";
import { paths } from "render/routes/paths";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { Link } from "render/ui/trigger/Link";
import { useCardioSecurDeeplink } from "render/views/CheckInCompleteView/hooks/useCardioSecurDeeplink";
import styles from "./styles.module.sass";

type CheckInCompleteViewProps = {
  goTo(url: string): void;
} & CheckInParams;

export function CheckInCompleteView({
  goTo,
  patientId,
  visitId,
}: CheckInCompleteViewProps) {
  const { data: visit } = useVisitQuery({
    patientId,
    visitId,
    options: { refetchInterval: 5000 },
  });

  const skinScanState = readScanStateFromVisit("skin", visit);
  const cardioScanState = readScanStateFromVisit("cardio", visit);
  const tissueScanState = readScanStateFromVisit("tci", visit);
  const ecgCardioSecurScanState = readScanStateFromVisit(
    "ecg-cardiosecur",
    visit
  );

  const goToNext = useCallback(() => {
    const url = paths.root.url({});
    goTo(url);
  }, [goTo]);

  const openCardioSecurApp = useCardioSecurDeeplink({
    patientId,
    visitId,
  });

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">Scan progress</Typography>
            <List>
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Skin Scan</Typography>
                    <Link
                      onPress={() =>
                        goTo(
                          paths.assignToSkinScanner.url({
                            patientId,
                            visitId,
                          })
                        )
                      }
                    >
                      Re-assign member
                    </Link>
                  </div>
                  {skinScanState != null ? (
                    <ScanStatusBadge scanState={skinScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              <List.Divider />
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Tissue Scan</Typography>
                  </div>
                  {tissueScanState != null ? (
                    <ScanStatusBadge scanState={tissueScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              <List.Divider />
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">ECG (12-lead)</Typography>
                    <div className={styles.itemActions}>
                      <Link onPress={openCardioSecurApp}>
                        {ecgCardioSecurScanState ===
                        APITypesV1.ImmutableScanState.Finished
                          ? "Re-assign member"
                          : "Assign to CardioSecur"}
                      </Link>
                      <Typography variant="body-m" color="subtle">
                        ({patientId})
                      </Typography>
                    </div>
                  </div>
                  {ecgCardioSecurScanState != null ? (
                    <ScanStatusBadge scanState={ecgCardioSecurScanState} />
                  ) : undefined}
                </div>
              </List.Item>
              <List.Divider />
              <List.Item>
                <div className={styles.scanItem}>
                  <div className={styles.itemTitle}>
                    <Typography variant="title-s">Cardio Scan</Typography>
                  </div>
                  {cardioScanState != null ? (
                    <ScanStatusBadge scanState={cardioScanState} />
                  ) : undefined}
                </div>
              </List.Item>
            </List>
            <div className={styles.divider} />
            <section className={styles.checkoutSection}>
              <div className={styles.checkoutText}>
                <Typography variant="title-l">Ready to check out?</Typography>
                <Typography variant="body-m" color="subtle">
                  Check out if all examinations are complete
                </Typography>
              </div>
              <Button
                onPress={goToNext}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Check Out
              </Button>
            </section>
          </section>
        }
      />
    </PageFrameContent>
  );
}

type ScanStatusBadgeProps = {
  scanState: APITypesV1.ImmutableScanState | undefined;
};

type ScanStatusBadgeHighlight = "success" | "error";

function ScanStatusBadge({ scanState }: ScanStatusBadgeProps) {
  const { label, highlight } = useMemo<{
    label: string;
    highlight?: ScanStatusBadgeHighlight;
  }>(() => {
    switch (scanState) {
      case APITypesV1.ImmutableScanState.Finished:
        return {
          label: "Completed",
          highlight: "success",
        };
      case APITypesV1.ImmutableScanState.Inprogress:
        return {
          label: "In progress",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Available:
        return {
          label: "Ready",
          highlight: undefined,
        };
      case APITypesV1.ImmutableScanState.Failed:
        return {
          label: "Missing",
          highlight: "error",
        };
      default:
        return {
          label: "",
          highlight: undefined,
        };
    }
  }, [scanState]);

  return (
    <div className={styles.scanStatusBadge} data-highlight={highlight}>
      {highlight === "success" && <Icon16Checkmark />}
      {highlight === "error" && <Icon16Alert />}
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}
